"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PeerManager = exports.getDistance = void 0;
const utils_1 = require("@streamr/utils");
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const k_bucket_1 = __importDefault(require("k-bucket"));
const identifiers_1 = require("../identifiers");
const RandomContactList_1 = require("./contact/RandomContactList");
const RingContactList_1 = require("./contact/RingContactList");
const SortedContactList_1 = require("./contact/SortedContactList");
const ringIdentifiers_1 = require("./contact/ringIdentifiers");
const logger = new utils_1.Logger(module);
// Returns all offline nodes, sets contacts as active if they are online
const pingNodes = async (nodes, activeContacts) => {
    const offlineNeighbors = [];
    await Promise.allSettled(nodes.map(async (contact) => {
        const isOnline = await contact.ping();
        if (!isOnline) {
            activeContacts.delete(contact.getNodeId());
            offlineNeighbors.push(contact.getPeerDescriptor());
        }
        else {
            activeContacts.add(contact.getNodeId());
        }
    }));
    return offlineNeighbors;
};
const getDistance = (nodeIdOrDataKeyRaw1, nodeIdOrDataKeyRaw2) => {
    return k_bucket_1.default.distance(nodeIdOrDataKeyRaw1, nodeIdOrDataKeyRaw2);
};
exports.getDistance = getDistance;
class PeerManager extends eventemitter3_1.default {
    // Glossary: 
    // * 'neighbors' are the nodes that are our neighbors according to 
    //   the protocol of the layer we are in
    // * 'connections' are the nodes that are connected to this node on Layer0
    // * 'contacts' are all non-unresponsive nodes that we know about
    neighbors;
    nearbyContacts;
    activeContacts;
    ringContacts;
    randomContacts;
    stopped = false;
    options;
    constructor(options) {
        super();
        this.options = options;
        this.neighbors = new k_bucket_1.default({
            localNodeId: (0, identifiers_1.toDhtAddressRaw)(this.options.localNodeId),
            numberOfNodesPerKBucket: this.options.numberOfNodesPerKBucket,
            numberOfNodesToPing: this.options.numberOfNodesPerKBucket
        });
        this.ringContacts = new RingContactList_1.RingContactList((0, ringIdentifiers_1.getRingIdRawFromPeerDescriptor)(this.options.localPeerDescriptor));
        this.ringContacts.on('contactAdded', (contact) => {
            this.emit('ringContactAdded', contact.getPeerDescriptor());
        });
        this.ringContacts.on('contactRemoved', (contact) => {
            this.emit('ringContactRemoved', contact.getPeerDescriptor());
        });
        this.neighbors.on('ping', (oldContacts, newContact) => this.onKBucketPing(oldContacts, newContact));
        this.neighbors.on('removed', (contact) => this.onKBucketRemoved((0, identifiers_1.toNodeId)(contact.getPeerDescriptor())));
        this.neighbors.on('added', (contact) => this.onKBucketAdded(contact));
        this.neighbors.on('updated', () => {
            // TODO: Update contact info to the connection manager and reconnect
        });
        this.nearbyContacts = new SortedContactList_1.SortedContactList({
            referenceId: this.options.localNodeId,
            maxSize: this.options.maxContactCount,
            allowToContainReferenceId: false
        });
        this.nearbyContacts.on('contactRemoved', (contact) => {
            if (this.stopped) {
                return;
            }
            this.emit('nearbyContactRemoved', contact.getPeerDescriptor());
            this.randomContacts.addContact(this.options.createDhtNodeRpcRemote(contact.getPeerDescriptor()));
        });
        this.nearbyContacts.on('contactAdded', (contact) => this.emit('nearbyContactAdded', contact.getPeerDescriptor()));
        this.activeContacts = new Set();
        this.randomContacts = new RandomContactList_1.RandomContactList(this.options.localNodeId, this.options.maxContactCount);
        this.randomContacts.on('contactRemoved', (removedContact) => this.emit('randomContactRemoved', removedContact.getPeerDescriptor()));
        this.randomContacts.on('contactAdded', (contactAdded) => this.emit('randomContactAdded', contactAdded.getPeerDescriptor()));
    }
    onKBucketPing(oldContacts, newContact) {
        if (this.stopped) {
            return;
        }
        const sortingList = new SortedContactList_1.SortedContactList({
            referenceId: this.options.localNodeId,
            allowToContainReferenceId: false
        });
        sortingList.addContacts(oldContacts);
        const removableNodeId = sortingList.getFurthestContacts(1)[0].getNodeId();
        this.options.connectionLocker?.weakUnlockConnection(removableNodeId, this.options.lockId);
        this.neighbors.remove((0, identifiers_1.toDhtAddressRaw)(removableNodeId));
        this.neighbors.add(newContact);
    }
    onKBucketRemoved(nodeId) {
        if (this.stopped) {
            return;
        }
        this.options.connectionLocker?.weakUnlockConnection(nodeId, this.options.lockId);
        logger.trace(`Removed contact ${nodeId}`);
        if (this.neighbors.count() === 0) {
            this.emit('kBucketEmpty');
        }
    }
    onKBucketAdded(contact) {
        if (this.stopped) {
            return;
        }
        if (contact.getNodeId() !== this.options.localNodeId) {
            const peerDescriptor = contact.getPeerDescriptor();
            const nodeId = (0, identifiers_1.toNodeId)(peerDescriptor);
            // Important to lock here, before the ping result is known
            this.options.connectionLocker?.weakLockConnection(nodeId, this.options.lockId);
            if (this.options.hasConnection(contact.getNodeId())
                || (this.options.neighborPingLimit !== undefined && this.neighbors.count() > this.options.neighborPingLimit)) {
                logger.trace(`Added new contact ${nodeId}`);
            }
            else { // open connection by pinging
                logger.trace('starting ping ' + nodeId);
                contact.ping().then((result) => {
                    if (result) {
                        logger.trace(`Added new contact ${nodeId}`);
                    }
                    else {
                        logger.trace('ping failed ' + nodeId);
                        this.options.connectionLocker?.weakUnlockConnection(nodeId, this.options.lockId);
                        this.removeContact(nodeId);
                        this.addNearbyContactToNeighbors();
                    }
                }).catch((_e) => {
                    this.options.connectionLocker?.weakUnlockConnection(nodeId, this.options.lockId);
                    this.removeContact(nodeId);
                    this.addNearbyContactToNeighbors();
                });
            }
        }
    }
    addNearbyContactToNeighbors() {
        if (this.stopped) {
            return;
        }
        const closest = this.getNearbyActiveContactNotInNeighbors();
        if (closest) {
            this.addContact(closest.getPeerDescriptor());
        }
    }
    getNearbyActiveContactNotInNeighbors() {
        for (const contactId of this.nearbyContacts.getContactIds()) {
            if (!this.neighbors.get((0, identifiers_1.toDhtAddressRaw)(contactId)) && this.activeContacts.has(contactId)) {
                return this.nearbyContacts.getContact(contactId);
            }
        }
        return undefined;
    }
    removeContact(nodeId) {
        if (this.stopped) {
            return;
        }
        logger.trace(`Removing contact ${nodeId}`);
        this.ringContacts.removeContact(this.nearbyContacts.getContact(nodeId));
        this.neighbors.remove((0, identifiers_1.toDhtAddressRaw)(nodeId));
        this.nearbyContacts.removeContact(nodeId);
        this.activeContacts.delete(nodeId);
        this.randomContacts.removeContact(nodeId);
    }
    removeNeighbor(nodeId) {
        this.neighbors.remove((0, identifiers_1.toDhtAddressRaw)(nodeId));
    }
    async pruneOfflineNodes(nodes) {
        logger.trace('Pruning offline nodes', { nodes: nodes.length });
        const offlineNeighbors = await pingNodes(nodes, this.activeContacts);
        offlineNeighbors.forEach((offlineNeighbor) => {
            logger.trace('Removing offline node', { node: (0, identifiers_1.toNodeId)(offlineNeighbor) });
            this.removeContact((0, identifiers_1.toNodeId)(offlineNeighbor));
        });
    }
    stop() {
        this.stopped = true;
        this.neighbors.toArray().forEach((rpcRemote) => {
            rpcRemote.leaveNotice();
            this.neighbors.remove(rpcRemote.id);
        });
        this.neighbors.removeAllListeners();
        this.ringContacts.getAllContacts().forEach((rpcRemote) => {
            rpcRemote.leaveNotice();
            this.ringContacts.removeContact(rpcRemote);
        });
        this.nearbyContacts.stop();
        this.randomContacts.stop();
    }
    getNearbyContacts() {
        return this.nearbyContacts;
    }
    getClosestRingContactsTo(ringIdRaw, limit, excludedIds) {
        const closest = new RingContactList_1.RingContactList(ringIdRaw, excludedIds);
        this.ringContacts.getAllContacts().map((contact) => closest.addContact(contact));
        // TODO use options option or named constant?
        return closest.getClosestContacts(limit ?? 8);
    }
    getRandomContacts() {
        return this.randomContacts;
    }
    getRingContacts() {
        return this.ringContacts;
    }
    getNearbyContactCount(excludedNodeIds) {
        return this.nearbyContacts.getSize(excludedNodeIds);
    }
    getNeighborCount() {
        return this.neighbors.count();
    }
    getNeighbors() {
        return this.neighbors.toArray();
    }
    setContactActive(nodeId) {
        this.activeContacts.add(nodeId);
    }
    addContact(peerDescriptor) {
        if (this.stopped) {
            return;
        }
        const nodeId = (0, identifiers_1.toNodeId)(peerDescriptor);
        if (nodeId !== this.options.localNodeId) {
            logger.trace(`Adding new contact ${nodeId}`);
            const remote = this.options.createDhtNodeRpcRemote(peerDescriptor);
            const isInNeighbors = (this.neighbors.get(peerDescriptor.nodeId) !== null);
            const isInNearbyContacts = (this.nearbyContacts.getContact(nodeId) !== undefined);
            const isInRingContacts = this.ringContacts.getContact(peerDescriptor) !== undefined;
            if (isInNeighbors || isInNearbyContacts) {
                this.randomContacts.addContact(remote);
            }
            if (!isInNeighbors) {
                this.neighbors.add(remote);
            }
            if (!isInNearbyContacts) {
                this.nearbyContacts.addContact(remote);
            }
            if (!isInRingContacts) {
                this.ringContacts.addContact(remote);
            }
        }
    }
}
exports.PeerManager = PeerManager;
//# sourceMappingURL=PeerManager.js.map